import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { publicUnsubscribe } from "@/lib/api";

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  // status: idle | confirming | working | done | error
  const [status, setStatus] = useState(token ? "confirming" : "error");
  const [email, setEmail] = useState("");
  const [error, setError] = useState(token ? "" : "Missing unsubscribe token in the link.");

  useEffect(() => {
    document.title = "UNSUBSCRIBE // THINKAVARIUM";
  }, []);

  const confirm = async () => {
    setStatus("working");
    try {
      const res = await publicUnsubscribe(token);
      setEmail(res.email || "");
      setStatus("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "This unsubscribe link is no longer valid.");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-void text-white noise-overlay">
      <header className="border-b border-white/10 bg-bunker-shadow/80 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5 font-mono text-[11px] tracking-[0.3em] md:px-10 md:text-[13px]">
          <Link to="/" className="flex items-center gap-3 text-white hover:text-hazard-amber" data-testid="unsub-back-home">
            <span className="hazard-stripes h-3 w-8" />
            THINKAVARIUM
          </Link>
          <span className="text-white/40">// TRANSMISSION OPT-OUT</span>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-24 md:px-10 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="mb-3 flex items-center gap-3 font-mono text-xs tracking-[0.3em] text-reactor-red md:text-[13px]">
            <span className="hazard-stripes h-3 w-10" />
            SIGNAL TERMINATION
          </div>
          <h1 className="font-display text-5xl uppercase leading-[0.9] text-white md:text-7xl">Leave the Frequency</h1>
          <p className="mt-6 max-w-xl font-body text-sm font-light leading-relaxed text-white/60 md:text-lg">
            Confirm below and your transmitter will be permanently removed from the mailing list. You will not receive any further broadcasts from THINKAVARIUM.
          </p>
        </motion.div>

        <div
          className="clip-hud mt-12 border border-hazard-amber/25 bg-bunker-shadow/70 p-6 backdrop-blur-md md:p-8"
          data-testid="unsub-panel"
        >
          {status === "confirming" && (
            <>
              <p className="mb-8 font-mono text-xs tracking-widest text-white/50 md:text-sm">
                &gt; Ready to sever the signal? This cannot be undone from here — you would need to re-enlist manually.
              </p>
              <button
                type="button"
                onClick={confirm}
                className="w-full border border-reactor-red bg-reactor-red px-8 py-4 font-mono text-xs font-bold tracking-[0.35em] text-void transition-colors duration-100 hover:bg-transparent hover:text-reactor-red md:text-[15px]"
                data-testid="unsub-confirm"
              >
                ✕ CONFIRM UNSUBSCRIBE
              </button>
              <Link
                to="/"
                className="mt-4 block text-center font-mono text-[11px] tracking-[0.25em] text-white/40 hover:text-hazard-amber md:text-sm"
                data-testid="unsub-cancel"
              >
                CANCEL · RETURN TO BUNKER
              </Link>
            </>
          )}

          {status === "working" && (
            <p className="font-mono text-sm tracking-[0.2em] text-hazard-amber" data-testid="unsub-working">
              &gt; TERMINATING SIGNAL...
            </p>
          )}

          {status === "done" && (
            <div data-testid="unsub-done">
              <p className="font-mono text-sm tracking-[0.2em] text-tactical-green md:text-base">
                &gt;&gt; SIGNAL TERMINATED
              </p>
              <p className="mt-3 font-body text-sm font-light leading-relaxed text-white/70 md:text-base">
                {email ? (
                  <>
                    <span className="font-mono text-white">{email}</span> has been removed from the mailing list.
                  </>
                ) : (
                  <>Your transmitter has been removed from the mailing list.</>
                )}{" "}
                You will not receive any further transmissions.
              </p>
              <Link
                to="/"
                className="mt-8 inline-block border border-white/25 px-6 py-3 font-mono text-[11px] tracking-[0.3em] text-white/80 hover:border-hazard-amber hover:text-hazard-amber md:text-sm"
                data-testid="unsub-return-home"
              >
                ← RETURN TO BUNKER
              </Link>
            </div>
          )}

          {status === "error" && (
            <div data-testid="unsub-error">
              <p className="font-mono text-sm tracking-[0.2em] text-reactor-red md:text-base">
                ✕ TRANSMISSION ERROR
              </p>
              <p className="mt-3 font-body text-sm font-light leading-relaxed text-white/70 md:text-base">
                {error}
              </p>
              <Link
                to="/"
                className="mt-8 inline-block border border-white/25 px-6 py-3 font-mono text-[11px] tracking-[0.3em] text-white/80 hover:border-hazard-amber hover:text-hazard-amber md:text-sm"
              >
                ← RETURN TO BUNKER
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
